import { useState, useMemo } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, UserPlus, KeyRound, Upload, CalendarCheck, FileQuestion } from "lucide-react";
import { Reveal, SectionHead, EASE } from "@/lib/motion";
import EnquiryForm from "@/components/EnquiryForm";
import PortalSignupForm from "@/components/PortalSignupForm";
import { COMPLEXITY_ITEMS } from "@/data/content";

const TYPE_HEADINGS = {
  "new-client": ["Start your tax return", "Answer a few questions — we take it from there."],
  estimate: ["Get a preparation estimate", "Tell us your situation and we'll come back with a clear quote before any work begins."],
  consultation: ["Schedule a consultation", "Thirty minutes, your questions, straight answers."],
  returning: ["Welcome back", "The fastest paths for returning clients."],
};

export default function GetStarted() {
  const [params] = useSearchParams();
  const initialType = params.get("type");
  const itemsParam = params.get("items");
  const statesParam = params.get("states");
  const initialItemIds = useMemo(() => {
    const ids = itemsParam ? itemsParam.split(",").filter(Boolean) : [];
    return COMPLEXITY_ITEMS.filter((i) => ids.includes(i.id)).map((i) => i.id);
  }, [itemsParam]);
  const initialStates = useMemo(
    () => (statesParam ? statesParam.split(",").filter(Boolean).map((s) => s.toUpperCase()) : []),
    [statesParam],
  );
  const [mode, setMode] = useState(initialType === "returning" ? "returning" : "new");
  const formType = ["estimate", "consultation"].includes(initialType) ? initialType : "new-client";
  const [heading, subheading] = TYPE_HEADINGS[formType] || TYPE_HEADINGS["new-client"];

  if (initialType === "consultation") return <Navigate to="/book" replace />;

  return (
    <div data-testid="get-started-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHead
            overline="Get Started"
            title={heading}
            copy={subheading}
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <button
            data-testid="new-client-tab"
            onClick={() => setMode("new")}
            className={`flex items-start gap-5 border p-8 text-left transition-colors duration-300 ${
              mode === "new" ? "border-steel-brass bg-steel-surface shadow-[0_0_24px_rgba(168,85,247,0.14)]" : "border-steel-border hover:border-steel-muted"
            }`}
          >
            <UserPlus className={`mt-1 h-6 w-6 ${mode === "new" ? "text-steel-brass" : "text-steel-muted"}`} />
            <div>
              <h3 className="font-display text-xl font-medium text-steel-text">New Client</h3>
              <p className="mt-2 text-sm text-steel-secondary">I'm new to Steele Industries.</p>
            </div>
          </button>
          <button
            data-testid="returning-client-tab"
            onClick={() => setMode("returning")}
            className={`flex items-start gap-5 border p-8 text-left transition-colors duration-300 ${
              mode === "returning" ? "border-steel-brass bg-steel-surface shadow-[0_0_24px_rgba(168,85,247,0.14)]" : "border-steel-border hover:border-steel-muted"
            }`}
          >
            <KeyRound className={`mt-1 h-6 w-6 ${mode === "returning" ? "text-steel-brass" : "text-steel-muted"}`} />
            <div>
              <h3 className="font-display text-xl font-medium text-steel-text">Returning Client</h3>
              <p className="mt-2 text-sm text-steel-secondary">I've worked with Steele Industries before.</p>
            </div>
          </button>
        </div>

        <AnimatePresence mode="wait">
          {mode === "new" ? (
            <motion.div
              key="new"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="mt-12 grid grid-cols-1 gap-16 lg:grid-cols-12"
            >
              <div className="lg:col-span-7">
                <Reveal>
                  {formType === "consultation" ? (
                    <EnquiryForm
                      type={formType}
                      showSituation
                      initialItems={initialItemIds}
                      initialStates={initialStates}
                      heading="Tell us about your situation"
                      subheading="Two minutes here saves twenty later."
                    />
                  ) : (
                    <PortalSignupForm
                      initialItems={initialItemIds}
                      initialStates={initialStates}
                      heading="Start your return"
                      subheading="We'll create your secure portal and email your sign-in details straight away."
                    />
                  )}
                </Reveal>
              </div>
              <div className="lg:col-span-5">
                <div className="space-y-6 lg:sticky lg:top-32">
                  {[
                    { icon: FileQuestion, title: "What happens next", desc: "We review your answers, confirm what your return involves, and give you a clear quote before any work begins." },
                    { icon: Upload, title: "Then documents", desc: "You upload your tax documents through the secure portal — never ordinary email." },
                    { icon: CalendarCheck, title: "Prefer to talk first?", desc: "Schedule a consultation instead. Same page, different door — select 'Schedule a Consultation' anywhere on the site." },
                  ].map((n, i) => (
                    <Reveal key={n.title} delay={i * 0.08}>
                      <div className="flex gap-5 border border-steel-border bg-steel-surface p-7">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
                          <n.icon className="h-5 w-5 text-steel-brass" />
                        </div>
                        <div>
                          <h4 className="font-display text-lg font-medium text-steel-text">{n.title}</h4>
                          <p className="mt-1.5 text-sm leading-relaxed text-steel-secondary">{n.desc}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="returning"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="mt-12"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {[
                  { icon: KeyRound, title: "Client Portal", desc: "Sign in to upload documents, check what we still need, and message us securely.", cta: "Open Portal", href: "/portal", testid: "returning-portal-btn" },
                  { icon: CalendarCheck, title: "Schedule Appointment", desc: "Book a consultation for this year's return or anything that's changed since.", cta: "Book a Call", href: "/book", testid: "returning-schedule-btn" },
                  { icon: FileQuestion, title: "Something Changed?", desc: "New job, new baby, new business, a notice in the mail — tell us and we'll adjust.", cta: "Send a Message", href: "/contact", testid: "returning-message-btn" },
                ].map((c) => (
                  <Reveal key={c.title}>
                    <div className="flex h-full flex-col justify-between border border-steel-border bg-steel-surface p-8">
                      <div>
                        <c.icon className="h-6 w-6 text-steel-brass" />
                        <h3 className="mt-5 font-display text-xl font-medium text-steel-text">{c.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-steel-secondary">{c.desc}</p>
                      </div>
                      <Link
                        to={c.href}
                        data-testid={c.testid}
                        onClick={c.onClick}
                        className="group mt-8 inline-flex items-center gap-2 border-b border-steel-brass pb-1 text-sm font-semibold text-steel-brass transition-colors hover:text-steel-text"
                      >
                        {c.cta}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.15}>
                <p className="mt-10 text-sm text-steel-muted">
                  Returning clients skip the marketing — the portal is your front door for documents, status
                  and scheduling.
                </p>
              </Reveal>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
